/**
 * Vax age-bucket parsing + due-date math + homepage alert tier logic.
 *
 * Age buckets come from the parent's booklet (free-text). Examples:
 *   "Birth", "6 Weeks", "10 Weeks", "14 Weeks",
 *   "6 Months", "9 Months", "15 Months", "18 Months", "18-24 Months",
 *   "Year 2", "Year 3" ... (yearly flu),
 *   "9 Years", "9 Years 6 Months", "10 Years", "15 Years 6 Months"
 */

import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { VaxScheduleEntry, VaxAlertTier } from '@/types';

const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;       // approximate; matches the booklet's intent
const DAYS_PER_YEAR = 365;

/**
 * Parse a free-text age label into days from DOB.
 * Returns 0 for "Birth"; returns NaN for unparseable input (caller decides fallback).
 */
export function parseAgeBucketDays(label: string): number {
  if (!label) return NaN;
  const s = label.trim().toLowerCase();
  if (s === 'birth' || s === 'at birth' || s === 'newborn') return 0;

  // "Year 2", "Year 7" — yearly flu rows
  const yearOnly = s.match(/^year\s+(\d+)$/);
  if (yearOnly) return parseInt(yearOnly[1], 10) * DAYS_PER_YEAR;

  // Range like "18-24 months" → use lower bound
  const range = s.match(/^(\d+)\s*[-–]\s*(\d+)\s*(week|weeks|month|months|year|years)/);
  if (range) {
    return convertToDays(parseInt(range[1], 10), range[3]);
  }

  // Composite: "9 Years 6 Months", "15 Years 0 Months", "1 Year 2 Months"
  const composite = s.match(/(\d+)\s*(year|years)\s+(\d+)\s*(month|months)/);
  if (composite) {
    return parseInt(composite[1], 10) * DAYS_PER_YEAR
         + parseInt(composite[3], 10) * DAYS_PER_MONTH;
  }

  // Single unit: "6 Weeks", "9 Months", "10 Years"
  const single = s.match(/(\d+)\s*(week|weeks|month|months|year|years|day|days)/);
  if (single) return convertToDays(parseInt(single[1], 10), single[2]);

  return NaN;
}

function convertToDays(n: number, unit: string): number {
  const u = unit.toLowerCase();
  if (u.startsWith('day')) return n;
  if (u.startsWith('week')) return n * DAYS_PER_WEEK;
  if (u.startsWith('month')) return n * DAYS_PER_MONTH;
  if (u.startsWith('year')) return n * DAYS_PER_YEAR;
  return NaN;
}

/** Compute the calendar date a vaccine is due (override > DOB+offset). */
export function computeDueDate(dob: Date, entry: Pick<VaxScheduleEntry, 'ageOffsetDays' | 'dueDateOverride'>): Date {
  if (entry.dueDateOverride) {
    const d = parseISO(entry.dueDateOverride);
    if (!isNaN(d.getTime())) return d;
  }
  const d = new Date(dob);
  d.setDate(d.getDate() + (Number.isFinite(entry.ageOffsetDays) ? entry.ageOffsetDays : 0));
  return d;
}

/**
 * Decide whether to surface this entry on the homepage today, and which tier.
 * Cadence: T-7 (7 days before), T-1 (1 day before), T-0 (on due date).
 *
 * Dismiss rules:
 *   - If user dismissed tier T-7 → don't show again until T-1 fires.
 *   - If user dismissed T-1 → don't show again until T-0 fires.
 *   - If user dismissed T-0 → don't show again unless they reschedule.
 *   - If appointment.status is 'rescheduled' with a date, due date becomes that date and tiers re-fire.
 */
export interface AlertDecision {
  shouldShow: boolean;
  tier: VaxAlertTier | 'overdue' | null;
  effectiveDueDate: Date;
  daysUntil: number;        // negative = overdue
}

export function decideAlert(
  entry: VaxScheduleEntry,
  dob: Date,
  today: Date = new Date()
): AlertDecision {
  if (entry.status !== 'scheduled') {
    return { shouldShow: false, tier: null, effectiveDueDate: computeDueDate(dob, entry), daysUntil: 0 };
  }

  // Rescheduled appointment overrides due date for alert calculation
  let effectiveDueDate = computeDueDate(dob, entry);
  if (entry.appointment?.status === 'rescheduled' && entry.appointment.date) {
    const r = parseISO(entry.appointment.date);
    if (!isNaN(r.getTime())) effectiveDueDate = r;
  }

  const daysUntil = differenceInCalendarDays(effectiveDueDate, today);

  // Decide which tier we're in
  let tier: AlertDecision['tier'] = null;
  if (daysUntil < 0) tier = 'overdue';
  else if (daysUntil === 0) tier = 'T-0';
  else if (daysUntil === 1) tier = 'T-1';
  else if (daysUntil <= 7) tier = 'T-7';
  else return { shouldShow: false, tier: null, effectiveDueDate, daysUntil };

  // If user has booked an appointment, suppress until the appointment date itself
  if (entry.appointment?.status === 'booked') {
    const apptDate = entry.appointment.date ? parseISO(entry.appointment.date) : null;
    if (apptDate && !isNaN(apptDate.getTime())) {
      const daysToAppt = differenceInCalendarDays(apptDate, today);
      if (daysToAppt > 0) return { shouldShow: false, tier, effectiveDueDate, daysUntil };
    }
  }

  // Dismiss suppression — only for non-overdue tiers
  if (entry.dismissedTier && tier !== 'overdue') {
    const tierOrder: Record<VaxAlertTier, number> = { 'T-7': 0, 'T-1': 1, 'T-0': 2 };
    const dismissedRank = tierOrder[entry.dismissedTier];
    const currentRank = tier === 'T-7' ? 0 : tier === 'T-1' ? 1 : 2;
    if (currentRank <= dismissedRank) {
      return { shouldShow: false, tier, effectiveDueDate, daysUntil };
    }
  }

  return { shouldShow: true, tier, effectiveDueDate, daysUntil };
}

/** Friendly label for an alert tier — used in card copy. */
export function tierLabel(tier: AlertDecision['tier'], daysUntil: number): string {
  if (tier === 'overdue') return `${Math.abs(daysUntil)}d overdue`;
  if (tier === 'T-0') return 'Due today';
  if (tier === 'T-1') return 'Due tomorrow';
  if (tier === 'T-7') return `Due in ${daysUntil} days`;
  return '';
}
